import { useRef, type KeyboardEvent, type ReactNode } from "react";

export interface TabDef<K extends string> {
  key: K;
  label: string;
  icon?: ReactNode;
}

/** Segmented tabs with roving focus (WAI-ARIA tabs pattern, automatic activation). */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  label,
  idBase,
  size = "md",
}: {
  tabs: TabDef<K>[];
  active: K;
  onChange(key: K): void;
  label: string;
  idBase: string;
  size?: "md" | "sm";
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;
    e.preventDefault();
    const tab = tabs[next];
    if (tab) {
      onChange(tab.key);
      refs.current[next]?.focus();
    }
  };
  return (
    <div className={`tabs${size === "sm" ? " tabs-sm" : ""}`} role="tablist" aria-label={label}>
      {tabs.map((tab, i) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${tab.key}`}
            aria-selected={selected}
            aria-controls={`${idBase}-panel-${tab.key}`}
            tabIndex={selected ? 0 : -1}
            className={`tab${selected ? " is-active" : ""}`}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
